/* eslint-disable sonarjs/no-duplicate-string */
import { faLocationDot } from "@fortawesome/pro-solid-svg-icons";

export const markerIconSVG = (color: string) => {
  const {
    icon: [width, height, , , svgPathData],
  } = faLocationDot;
  return window.btoa(
    `<svg xmlns="http://www.w3.org/2000/svg" fill="${color}" width="32px" height="32px" viewBox="0 0 ${width} ${height}">
      <path d="${svgPathData}" />
    </svg>`
  );
};

export const markerIconURL = (
  color = "#1a5e8a" // Default color
) => `data:image/svg+xml;base64,${markerIconSVG(color)}`;

/**
 * Extracts [lat, lng] from a data item with possible nested collection/block/coordinates.
 * Returns [null, null] if not found or invalid.
 * Handles both PropAI API response structures: direct and nested data.
 */
export function extractLatLng(item: any): [number | null, number | null] {
  // Try multiple possible data structures from PropAI API
  let coordinates;

  // Structure 1: item.data.collection[0].block.coordinates (original)
  if (Array.isArray(item?.data?.collection) && item.data.collection.length > 0) {
    coordinates = item.data.collection[0].block?.coordinates;
  }

  // Structure 2: item.data.data.collection[0].block.coordinates (PropAI nested)
  if (
    !coordinates &&
    Array.isArray(item?.data?.data?.collection) &&
    item.data.data.collection.length > 0
  ) {
    coordinates = item.data.data.collection[0].block?.coordinates;
  }

  // Structure 3: Direct coordinates in data
  if (!coordinates && item?.data?.coordinates) {
    coordinates = item.data.coordinates;
  }

  // Structure 4: Direct coordinates in nested data
  if (!coordinates && item?.data?.data?.coordinates) {
    coordinates = item.data.data.coordinates;
  }

  // Structure 5: Direct coordinates on item (FlexStation structure)
  if (!coordinates && item?.coordinates) {
    coordinates = item.coordinates;
  }

  // Parse coordinates if found
  if (typeof coordinates === "string" && coordinates.length > 0) {
    const [lat, lng] = coordinates.split(",").map(Number);
    if (!isNaN(lat) && !isNaN(lng)) return [lat, lng];
  }

  return [null, null];
}

/**
 * Calculates the geographic center point from an array of address data items.
 * Returns null if no valid coordinates are found.
 */
export function calculateMapCenter(installAddressData: any[]): { lat: number; lng: number } | null {
  if (!installAddressData || installAddressData.length === 0) {
    return null; // Will fall back to default center
  }

  const validCoordinates = installAddressData
    .map((item) => {
      const [lat, lng] = extractLatLng(item);
      return { lat, lng };
    })
    .filter((coord) => coord.lat && coord.lng && !isNaN(coord.lat) && !isNaN(coord.lng));

  if (validCoordinates.length === 0) {
    return null;
  }

  // Calculate the center point
  const totalLat = validCoordinates.reduce((sum, coord) => sum + coord.lat, 0);
  const totalLng = validCoordinates.reduce((sum, coord) => sum + coord.lng, 0);

  return {
    lat: totalLat / validCoordinates.length,
    lng: totalLng / validCoordinates.length,
  };
}
